// Text drawn ON TOP of a background image (banner / section-card images) must
// contrast the IMAGE, not the venue theme, otherwise a light theme paints
// dark text over a dark photo (or vice-versa) and it disappears. Admins pick a
// text colour (light/dark) + scrim opacity; we derive a matching gradient so
// the text stays readable over any photo.

export type ImageOverlay = {
  // Colour of text laid over images. "light" = white text on a dark scrim
  // (the default, works over most photos); "dark" = near-black text on a
  // light scrim (for bright/pale images).
  text?: "light" | "dark";
  // Scrim strength, 0–90 (%). Higher = the photo is dimmed more so text pops.
  opacity?: number;
};

export type OverlayColors = {
  /** gradient stops, strongest → softest */
  strong: string;
  mid: string;
  soft: string;
  /** heading text colour */
  fg: string;
  /** secondary text colour */
  sub: string;
};

export function overlayColors(cfg?: ImageOverlay): OverlayColors {
  const lightText = (cfg?.text ?? "light") !== "dark";
  const op = Math.min(90, Math.max(0, cfg?.opacity ?? 60)) / 100;
  // light text → dark scrim (black); dark text → light scrim (white)
  const base = lightText ? "0, 0, 0" : "255, 255, 255";
  return {
    strong: `rgba(${base}, ${Math.min(0.95, op + 0.25).toFixed(2)})`,
    mid: `rgba(${base}, ${op.toFixed(2)})`,
    soft: `rgba(${base}, ${Math.max(0, op - 0.2).toFixed(2)})`,
    fg: lightText ? "#ffffff" : "#111111",
    sub: lightText ? "rgba(255, 255, 255, 0.85)" : "rgba(0, 0, 0, 0.72)",
  };
}

/** Horizontal scrim (icon-left feature cards). */
export function overlayScrimHorizontal(c: OverlayColors): string {
  return `linear-gradient(to right, ${c.strong}, ${c.mid}, ${c.soft})`;
}

/** Vertical scrim (banner / hero cards: clear at top, dark at bottom). */
export function overlayScrimVertical(c: OverlayColors): string {
  return `linear-gradient(180deg, ${c.soft}, ${c.strong})`;
}
